import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import { useTranslation } from "../../hooks/useTranslation";
import BeforeAfterSlider from "../../components/BeforeAfterSlider";
import { V2, pickLang } from "../copy";
import { Arrow } from "./Primitives";
import { route } from "../paths";

const PAIRS = [
  { before: "/dirty2.webp", after: "/clean2.webp" },
  { before: "/dirty.webp", after: "/clean.webp" },
  { before: "/dirty3.webp", after: "/clean3.webp" },
];

/** Rotating circular stamp — pure SVG textPath, no library. */
const Stamp: React.FC<{ text: string }> = ({ text }) => (
  <div className="relative h-32 w-32 sm:h-40 sm:w-40">
    <svg viewBox="0 0 100 100" className="v2-spin h-full w-full" aria-hidden>
      <defs>
        <path
          id="v2-stamp-path"
          d="M 50,50 m -37,0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0"
        />
      </defs>
      <text
        fill="#F2EEE6"
        fontSize="8.2"
        letterSpacing="1.6"
        style={{ fontFamily: '"JetBrains Mono", monospace' }}
      >
        <textPath href="#v2-stamp-path">{text}</textPath>
      </text>
    </svg>
    <div className="absolute inset-0 flex items-center justify-center">
      <span className="block h-3 w-3 rounded-full bg-tangerine" />
    </div>
  </div>
);

const HeroV2: React.FC = () => {
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation();
  const lang = pickLang(currentLanguage);
  const c = V2[lang].hero;
  const brands = V2[lang].brands;

  const [ready, setReady] = useState(false);
  // The 60fps clip-path sweep repaints a full-width photo on every frame:
  // keep it for mouse users, let touch users drag the handle instead.
  const [finePointer] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(pointer: fine)").matches,
  );
  const [idx, setIdx] = useState(0);
  const [hover, setHover] = useState(false);

  useEffect(() => {
    const id = window.setTimeout(() => setReady(true), 80);
    return () => window.clearTimeout(id);
  }, []);

  // Preload the first before/after pair (the LCP image). The pre-render
  // snapshot keeps these <link>s in <head>, so real visitors get them early.
  useEffect(() => {
    const links = [PAIRS[0].after, PAIRS[0].before].map((href) => {
      if (document.head.querySelector(`link[rel="preload"][href="${href}"]`)) return null;
      const l = document.createElement("link");
      l.rel = "preload";
      l.as = "image";
      l.href = href;
      l.setAttribute("fetchpriority", "high");
      document.head.appendChild(l);
      return l;
    });
    return () => links.forEach((l) => l?.remove());
  }, []);

  // Auto-rotate the before/after pairs unless the visitor is on the slider.
  useEffect(() => {
    if (hover) return;
    const id = window.setInterval(
      () => setIdx((i) => (i + 1) % PAIRS.length),
      7000,
    );
    return () => window.clearInterval(id);
  }, [hover]);

  // Word-by-word masked reveal across the styled title parts.
  let wordIndex = 0;
  const title = c.title.map((part, p) => {
    const words = part.text.split(" ").filter(Boolean);
    const leading = part.text.startsWith(" ") ? " " : "";
    const trailing = part.text.endsWith(" ") ? " " : "";
    const cls = [
      part.italic ? "italic" : "",
      part.accent ? "text-tangerine" : "",
    ].join(" ");
    return (
      <React.Fragment key={p}>
        {leading}
        {words.map((w, i) => {
          const d = wordIndex++ * 0.06;
          return (
            <React.Fragment key={i}>
              <span className="v2-word">
                <span className={cls} style={{ transitionDelay: `${0.15 + d}s` }}>
                  {w}
                </span>
              </span>
              {i < words.length - 1 ? " " : ""}
            </React.Fragment>
          );
        })}
        {trailing}
      </React.Fragment>
    );
  });

  return (
    <section
      id="v2-home"
      className={`relative isolate overflow-hidden bg-ink pb-8 pt-24 text-bone sm:pt-36 ${
        ready ? "is-in" : ""
      }`}
    >
      {/* atmospheric glow */}
      <div className="v2-glow pointer-events-none absolute -top-40 right-[-15%] -z-10 h-[70vh] w-[70vh] rounded-full bg-tangerine/[0.12] blur-[140px]" />
      <div className="v2-glow pointer-events-none absolute -bottom-40 left-[-10%] -z-10 h-[50vh] w-[50vh] rounded-full bg-bone/[0.05] blur-[120px]" />

      <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
        {/*
          Three cells so the DOM order works on every screen:
          mobile  → headline, visual, copy (the picture lands right under the title)
          desktop → headline + copy stacked left, visual spanning both rows right
        */}
        <div className="grid items-center gap-x-8 gap-y-10 lg:grid-cols-12 lg:grid-rows-[auto_auto]">
          {/* 1. headline */}
          <div className="lg:col-span-7 lg:row-start-1">
            <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.22em] text-bone/70 sm:text-[11px]">
              <span className="relative flex h-2 w-2 flex-shrink-0">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-teal-light opacity-70" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-teal-light" />
              </span>
              {c.eyebrow}
            </div>

            <h1 className="mt-5 max-w-[12ch] font-display text-[clamp(3rem,7.2vw,7rem)] font-normal leading-[0.92] tracking-[-0.025em] sm:mt-6">
              {title}
            </h1>
          </div>

          {/* 2. visual */}
          <div className="relative lg:col-span-5 lg:col-start-8 lg:row-span-2 lg:row-start-1">
            <div
              className="v2-reveal relative"
              data-delay="2"
              onMouseEnter={() => setHover(true)}
              onMouseLeave={() => setHover(false)}
            >
              <div className="mb-3 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.22em] text-bone/60">
                <span>{c.before}</span>
                <span className="flex items-center gap-2">
                  <span className="h-px w-6 bg-teal/70" />
                  {c.drag}
                  <span className="h-px w-6 bg-teal/70" />
                </span>
                <span className="text-tangerine">{c.after}</span>
              </div>

              <div className="overflow-hidden rounded-[6px] ring-1 ring-bone/15">
                <BeforeAfterSlider
                  pairs={PAIRS}
                  alt="Sneaker cleaning — before and after"
                  initialPosition={50}
                  autoAnimate={finePointer}
                  animationDuration={3600}
                  currentPairIndex={idx}
                  onPairChange={setIdx}
                  className="cursor-col-resize"
                  circleClassName="!h-14 !w-14 !border-0 !bg-tangerine shadow-[0_0_0_10px_rgba(255,79,31,0.22)] [&_*]:!text-white"
                />
              </div>

              {/* pair selector — 32px tall hit areas for thumbs */}
              <div className="mt-2 flex items-center justify-between">
                <div className="flex items-center gap-1" role="group" aria-label="Pairs">
                  {PAIRS.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setIdx(i)}
                      aria-label={`Pair ${i + 1}`}
                      aria-pressed={i === idx}
                      className="flex h-8 items-center px-1"
                    >
                      <span
                        className={`block h-1 rounded-full transition-all duration-500 ${
                          i === idx ? "w-10 bg-tangerine" : "w-4 bg-bone/25 hover:bg-bone/50"
                        }`}
                      />
                    </button>
                  ))}
                </div>
                <span className="font-mono text-[10px] tracking-[0.2em] text-bone/50">
                  {`0${idx + 1} / 0${PAIRS.length}`}
                </span>
              </div>

              {/* stamp */}
              <div className="absolute -bottom-14 -left-6 hidden sm:block lg:-left-14">
                <Stamp text={c.stamp} />
              </div>
            </div>
          </div>

          {/* 3. copy + CTAs + stats */}
          <div className="lg:col-span-7 lg:row-start-2">
            <p
              className="v2-reveal max-w-md text-base leading-relaxed text-bone/70 sm:text-lg"
              data-delay="3"
            >
              {t("hero.description")}
            </p>

            <div className="v2-reveal mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap" data-delay="4">
              <Link to={`/${lang}/signup`} className="v2-btn v2-btn-solid justify-center sm:justify-start">
                {c.cta1}
                <Arrow />
              </Link>
              <Link to={route(lang, "devis")} className="v2-btn v2-btn-ghost justify-center sm:justify-start">
                {c.cta2}
              </Link>
            </div>

            <ul
              className="v2-hair v2-reveal mt-9 flex flex-wrap gap-x-10 gap-y-4 border-t pt-5"
              data-delay="5"
            >
              {c.stats.map(([v, l]) => (
                <li key={l}>
                  <div className="font-display text-3xl leading-none sm:text-4xl">
                    {v}
                  </div>
                  <div className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-bone/50">
                    {l}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Brand ticker */}
      <div className="v2-hair mt-16 overflow-hidden border-y py-4 sm:mt-28">
        <div className="v2-marquee">
          {[...brands, ...brands].map((b, i) => (
            <span key={i} className="flex items-center gap-8 px-5">
              <span className="whitespace-nowrap font-display text-3xl italic text-bone/60 sm:text-4xl">
                {b}
              </span>
              <span className="h-1.5 w-1.5 rounded-full bg-tangerine" />
            </span>
          ))}
        </div>
      </div>

      {/* scroll hint */}
      <div className="mx-auto mt-6 flex max-w-[1400px] items-center gap-4 px-4 font-mono text-[10px] uppercase tracking-[0.22em] text-bone/40 sm:px-6">
        <span className="v2-drop block h-10 w-px bg-teal-light/70" />
        {c.scroll}
      </div>
    </section>
  );
};

export default HeroV2;
